<template>
    <div class="form-tabs">
        <div class="tab-header">
            <div
                v-for="(tab, index) in tabs"
                :key="index"
                :class="['tab-title', { active: index === activeTab }]"
                @click="selectTab(index)"
            >
                {{ tab.title }}
                <span v-if="tabStatus[index] === true" class="tab-check"></span>
            </div>
        </div>
        <div class="tab-header-mobile">
            <DTSelect
                ref="tabHeaderSelect"
                :options="selectOptions"
                eventBusEmit="selectTab"
            />
        </div>
        <div
            v-for="(tab, index) in tabs"
            :key="index"
            :class="['tab-content', { active: index === activeTab }]"
            :data-payload-prefix="tab.prefix"
            :data-tab-index="index"
        >
            <slot></slot>
        </div>
    </div>
</template>

<script>
import { nextTick } from "vue";
import EventBus from "../event-bus";
import DTSelect from "./DTSelect.vue";

export default {
    name: "FormTabs",
    props: {
        tabs: {
            type: Array,
            required: true,
            default: () => [],
        },
    },
    components: {
        DTSelect,
    },
    data() {
        return {
            activeTab: 0,
            tabStatus: this.tabs.map(() => false),
            selectOptions: this.tabs.map((tab, index) => {
                let selected = false;
                if (index === 0) {
                    selected = true;
                }

                return {
                    value: index,
                    text: tab.title,
                    selected: selected,
                    status: false,
                };
            }),
            externalButtonDefaultText: "",
        };
    },
    inject: ["pageSettings"],
    methods: {
        goToTopOfFormTabs() {
            setTimeout(() => {
                const formTabs = document.querySelector(".form-tabs");
                if (formTabs) {
                    const offset =
                        formTabs.getBoundingClientRect().top +
                        window.scrollY -
                        32;
                    window.scrollTo({
                        top: offset,
                        behavior: "smooth",
                    });
                }
            }, 100);
        },
        selectTab(index) {
            if (index === this.activeTab) {
                return;
            }

            this.activeTab = index;
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    if (this.$refs.tabHeaderSelect) {
                        this.$refs.tabHeaderSelect.setOption(this.activeTab);
                    }
                });
            });

            this.goToTopOfFormTabs();
            this.controlTabStatus();
            this.controlExternalButton();

            EventBus.emit("tabChanged", [this.activeTab, this.tabs.length]);
        },
        nextTab() {
            if (this.activeTab < this.tabs.length - 1) {
                this.activeTab++;

                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        if (this.$refs.tabHeaderSelect) {
                            this.$refs.tabHeaderSelect.setOption(
                                this.activeTab
                            );
                        }
                    });
                });

                this.goToTopOfFormTabs();
                this.controlTabStatus();
                this.controlExternalButton();

                EventBus.emit("tabChanged", [this.activeTab, this.tabs.length]);
                return;
            }

            if (this.activeTab === this.tabs.length - 1) {
                this.controlTabStatus();
                this.$router.push(this.pageSettings.step2Url);
            }
        },
        controlTabStatus() {
            const tabs = document.querySelectorAll(".tab-content");
            tabs.forEach((tab, index) => {
                const inputs = tab.querySelectorAll(
                    ".dt-input, .dt-select, .dt-color-radio"
                );

                let allInputsValid = true;
                inputs.forEach((input) => {
                    if (input.getAttribute("required") === null) {
                        return;
                    }

                    if (input.classList.contains("dt-select")) {
                        if (
                            input
                                .querySelector(".value")
                                .getAttribute("data-value") === ""
                        ) {
                            allInputsValid = false;
                            return;
                        }
                    }

                    if (input.classList.contains("dt-color-radio")) {
                        if (
                            !input.querySelector(
                                ".dt-color-radio-option:checked"
                            )
                        ) {
                            allInputsValid = false;
                            return;
                        }
                    }

                    if (input.classList.contains("dt-input")) {
                        if (input.value === "") {
                            allInputsValid = false;
                            return;
                        }
                    }
                });

                if (allInputsValid) {
                    this.tabStatus[index] = true;
                    this.selectOptions[index].status = true;
                } else {
                    this.tabStatus[index] = false;
                    this.selectOptions[index].status = false;
                }
            });
        },
        controlExternalButton() {
            const externalButtons = document.querySelectorAll(
                'button[data-eventbusemit="nextTab"]'
            );

            externalButtons.forEach((button) => {
                if (this.externalButtonDefaultText === "") {
                    this.externalButtonDefaultText = button.innerText;
                }

                if (this.activeTab < this.tabs.length - 1) {
                    button.innerText = this.externalButtonDefaultText;
                } else {
                    button.innerText = "Weiter";
                }
            });
        },
        setTabOnError() {
            this.controlTabStatus();

            for (let i = 0; i < this.tabStatus.length; i++) {
                if (this.tabStatus[i] === false) {
                    this.selectTab(i);
                    break;
                }
            }
        },
    },
    mounted() {
        EventBus.on("nextTab", this.nextTab);
        EventBus.on("selectTab", this.selectTab);
        EventBus.on("throwErrorForRequiredInputs", this.setTabOnError);

        requestAnimationFrame(() => {
            nextTick(() => {
                this.controlTabStatus();
            });
        });
    },
    beforeUnmount() {
        EventBus.off("nextTab", this.nextTab);
        EventBus.off("selectTab", this.selectTab);
        EventBus.off("throwErrorForRequiredInputs", this.setTabOnError);
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.vars.scss" as *;
.form-tabs {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    row-gap: 32px;
    width: 100%;

    .tab-header {
        display: flex;
        flex-wrap: nowrap;
        flex-direction: row;
        justify-content: space-between;
        align-items: stretch;
        column-gap: 16px;
        width: 100%;
        padding: 8px;
        border-radius: 32px;
        border: 1px solid $color-light-gray;
        background: rgba(255, 255, 255, 0.5);

        @media (max-width: $breakpoint-tablet) {
            display: none;
        }

        .tab-title {
            display: flex;
            flex-wrap: nowrap;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            column-gap: 5px;
            width: 100%;
            padding: 10px 16px;
            border-radius: 40px;
            cursor: pointer;
            line-height: 35px;
            transition: background-color 0.3s, color 0.3s;

            &:hover,
            &.active {
                background-color: $color-new-black;
                box-shadow: 0px 3px 12px 0px rgba(181, 94, 43, 0.2);
                color: $color-white;
            }

            &.active {
                .tab-check {
                    display: none;
                }
            }

            .tab-check {
                display: inline-block;
                width: 15px;
                height: 15px;
                background-image: url("~@/assets/customizer_icon_check_green.svg");
                background-size: cover;
                background-position: center center;
            }
        }
    }

    .tab-header-mobile {
        display: none;
        width: 100%;

        @media (max-width: $breakpoint-tablet) {
            display: block;
        }
    }

    .tab-content {
        display: none;

        &.active {
            display: block;
        }
    }
}
</style>
